import { CircleLazyImage, ELoadingType, EMGirlType, Loading, MgirlNotice } from '@bilibili-maoer/prelude'
import { api, format } from '@bilibili-maoer/web-utils'

import { useEffect } from 'react'

import styled from '@emotion/styled'
import { Card, Divider, Link, Pagination, Stack, css } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { get } from 'lodash'
import { match } from 'ts-pattern'

import { Typo } from '~/core/components/alias'
import { StyledPaper as Paper } from '~/core/components/layout'
import { Title } from '~/core/components/title'
import { asMissEvanPagination } from '~/core/pagination'
import { backgroundImage, textOverflow } from '~/core/styles'
import { CENTER_MIDDLE_TITLE } from '~/features/center/constants'
import { RoomRankItem } from '~/features/room/types'
import { RoomApi } from '~/services/fm/room'
import { UserApi } from '~/services/fm/user'

import userPointImg from '/assets/icons/user/center/user-point@2x.png'

const LIST_LENGTH = 10
const rankColor = (rank: number) => {
  switch (rank) {
    case 1:
      return '#ffc400'
    case 2:
      return '#9ec0cd'
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    case 3:
      return '#dd9c5b'
    default:
      return undefined
  }
}

const StyledPaper = styled(Paper)<{ theme?: Theme }>(
  ({ theme }) => css`
    justify-content: center;
    width: 1140px;
    min-height: 712px;
    margin: 0 auto;
    background-color: ${theme.palette.background.default};
    transition: width 0.5s;
    @media screen and (max-width: 1399px) {
      width: 980px;
    }
  `
)

const StyledContainer = styled(Stack)`
  height: 500px;
  padding: 0 30px;
  margin-bottom: 30px;
`
const StyledPaginationContainer = styled('div')`
  display: flex;
  justify-content: center;
`
const FormattedMgirlNotFound = styled(MgirlNotice)`
  text-align: center;
  img {
    height: 170px;
  }
`
const StyledCard = styled(Card)`
  width: 100%;
  height: 50px;
  line-height: 50px;
  display: flex;
  align-items: center;
  font-size: 16px;
  background: none;
`

const StyledRank = styled(Typo)<{ rank: number; theme?: Theme }>(
  ({ rank, theme }) => css`
    width: 22px;
    margin-right: 20px;
    font-size: 16px;
    color: ${rankColor(rank) || theme.palette.primary.light};
    flex: none;
  `
)
const StyledUsername = styled(Link)<{ rank: number; theme?: Theme }>(
  ({ rank, theme }) => css`
    ${textOverflow('260px')}
    color: ${rankColor(rank)};
    transition: opacity 0.3s, width 0.5s;
    flex: none;
    &:hover {
      color: ${rankColor(rank) || theme.palette.text.primary};
      opacity: 0.7;
    }
    @media screen and (max-width: 1399px) {
      ${textOverflow('180px')}
    }
  `
)
const StyledUserCover = styled(Typo)<{ rank: number }>(
  ({ rank }) => css`
    width: 34px;
    height: 34px;
    margin-right: 30px;
    border: 2px solid ${rankColor(rank) || 'white'};
    border-radius: 50%;
    overflow: hidden;
    flex: none;
  `
)
const StyledUserPoint = styled(Typo)`
  color: #ff7861;
  padding-left: 20px;
  display: flex;
  align-items: center;
  :before {
    content: '';
    display: block;
    margin-right: 5px;
    ${backgroundImage(userPointImg, '14px', '13px')}
  }
`
const UserRankingCard = ({ rankInfo }: { rankInfo: RoomRankItem }) => {
  const { rank, iconurl, username, user_id: userId, revenue } = rankInfo
  return (
    <StyledCard elevation={0}>
      <StyledRank rank={rank}>{rank}</StyledRank>
      <StyledUserCover rank={rank}>
        <CircleLazyImage src={iconurl || ''} alt={username || ''} width={30} height={30} />
      </StyledUserCover>
      <StyledUsername
        rank={rank}
        href={`${api.MISSEVAN_SITE_MAIN}/${userId}`}
        underline="none"
        target="_blank"
        title={username}
      >
        {username}
      </StyledUsername>
      {/* 与 App 一致的数据简化规则，超过 10w 时简化，保留 1 位小数 */}
      <StyledUserPoint>{format.formatCount(revenue, { limit: 10000 })}</StyledUserPoint>
    </StyledCard>
  )
}

export const Ranking = () => {
  const { data: userInfo } = UserApi.getUserInfo.useQuery()
  const roomId = get(userInfo, 'room.room_id')

  const [trigger, data] = RoomApi.getChatroomRank.useLazyQuery()
  const { data: roomRankData, isFetching, isError } = data
  useEffect(() => {
    if (roomId) {
      trigger({ roomId })
    }
  }, [roomId, trigger])
  const rankList = roomRankData?.Datas || roomRankData?.data
  const pagination = roomRankData?.pagination && asMissEvanPagination(roomRankData?.pagination)
  const handlePageChange = (page: number) => {
    trigger({ roomId, p: page })
  }
  return (
    <StyledPaper sx={{ height: 'auto', justifyContent: 'center', minWidth: '1200px' }}>
      {match({ isFetching, rankListLength: !!rankList?.length, isError })
        .with({ isFetching: true }, () => <Loading type={ELoadingType.Cat} />)
        .with({ isError: true }, () => (
          <FormattedMgirlNotFound image={EMGirlType.NotFound} description="网页出现错误~" />
        ))
        .with({ rankListLength: false }, () => (
          <FormattedMgirlNotFound image={EMGirlType.NotFound} description="还没有收到过礼物~" />
        ))
        .otherwise(() => (
          <>
            <Title>{['贡献榜', CENTER_MIDDLE_TITLE]}</Title>
            <StyledContainer
              spacing={'30px'}
              useFlexGap
              direction="row"
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Typo component="div" sx={{ width: '50%' }}>
                {rankList &&
                  rankList
                    .slice(0, LIST_LENGTH)
                    .map((rankInfo) => <UserRankingCard key={rankInfo.user_id} rankInfo={rankInfo} />)}
              </Typo>
              <Typo component="div" sx={{ width: '50%' }}>
                {rankList &&
                  rankList
                    .slice(LIST_LENGTH, rankList.length)
                    .map((rankInfo) => <UserRankingCard key={rankInfo.user_id} rankInfo={rankInfo} />)}
              </Typo>
            </StyledContainer>
            <StyledPaginationContainer>
              <Pagination
                page={pagination?.page}
                count={pagination?.page_count}
                showFirstButton
                showLastButton
                onChange={(_, page) => handlePageChange(page)}
              />
            </StyledPaginationContainer>
          </>
        ))}
    </StyledPaper>
  )
}
